import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Eye, Thermometer } from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";

import { weatherService } from "../../../services/weather";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";

interface WeatherWidgetProps {
  coordinates: [number, number];
  eventDate: Date;
}

const WeatherWidget: React.FC<WeatherWidgetProps> = ({ coordinates, eventDate }) => {
  const [weather, setWeather] = useState<any>(null);
  const [forecast, setForecast] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setIsLoading(true);
        const [currentWeather, weatherForecast] = await Promise.all([
          weatherService.getCurrentWeather(coordinates[1], coordinates[0]),
          weatherService.getWeatherForecast(coordinates[1], coordinates[0]),
        ]);
        setWeather(currentWeather);
        setForecast(weatherForecast);
      } catch (_err) {
        setError("Failed to load weather data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeather();
  }, [coordinates]);

  const getWeatherIcon = (condition: string) => {
    switch (condition.toLowerCase()) {
      case "clear":
      case "sunny":
        return <Sun className='h-8 w-8 text-yellow-500' />;
      case "clouds":
      case "cloudy":
        return <Cloud className='h-8 w-8 text-gray-500' />;
      case "rain":
      case "drizzle":
        return <CloudRain className='h-8 w-8 text-blue-500' />;
      case "snow":
        return <CloudSnow className='h-8 w-8 text-blue-200' />;
      default:
        return <Cloud className='h-8 w-8 text-gray-500' />;
    }
  };

  const isGoodWeather = weather ? weatherService.isGoodWeatherForOutdoorEvent(weather) : true;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Weather Forecast</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center gap-4'>
            <Skeleton className='h-16 w-16 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-6 w-24' />
              <Skeleton className='h-4 w-32' />
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <Skeleton className='h-16' />
            <Skeleton className='h-16' />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='p-6 text-center'>
          <p className='text-muted-foreground'>{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Current Weather */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle>Current Weather</CardTitle>
          <Badge variant={isGoodWeather ? "default" : "destructive"}>
            {isGoodWeather ? "Good for Events" : "Weather Alert"}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className='mb-4 flex items-center gap-4'>
            {getWeatherIcon(weather.condition)}
            <div>
              <div className='text-3xl font-bold'>{weather.temperature}°C</div>
              <div className='capitalize text-muted-foreground'>{weather.description}</div>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4 text-sm md:grid-cols-4'>
            <div className='flex items-center gap-2'>
              <Thermometer className='h-4 w-4 text-muted-foreground' />
              <div>
                <div className='font-medium'>Feels like</div>
                <div className='text-muted-foreground'>{weather.feelsLike}°C</div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Droplets className='h-4 w-4 text-muted-foreground' />
              <div>
                <div className='font-medium'>Humidity</div>
                <div className='text-muted-foreground'>{weather.humidity}%</div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Wind className='h-4 w-4 text-muted-foreground' />
              <div>
                <div className='font-medium'>Wind</div>
                <div className='text-muted-foreground'>{weather.windSpeed} m/s</div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Eye className='h-4 w-4 text-muted-foreground' />
              <div>
                <div className='font-medium'>Visibility</div>
                <div className='text-muted-foreground'>{weather.visibility} km</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 5-Day Forecast */}
      <Card>
        <CardHeader>
          <CardTitle>5-Day Forecast</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-5'>
            {forecast.map((day, index) => (
              <div key={index} className='rounded-lg border p-3 text-center'>
                <div className='mb-2 text-sm font-medium'>
                  {index === 0
                    ? "Today"
                    : new Date(day.date).toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div className='mb-2 flex justify-center'>{getWeatherIcon(day.condition)}</div>
                <div className='text-sm'>
                  <div className='font-medium'>{day.temperature.max}°</div>
                  <div className='text-muted-foreground'>{day.temperature.min}°</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeatherWidget;
